// Keeps an embedded activities iframe sized to fit its content by measuring
// the page and posting `adjustHeight` messages to the parent window.
//
// This runs identically for single- and multi-iframe embeds. A one-shot
// measurement can fire mid-reflow (e.g. before web fonts/images settle) and,
// without continuous tracking, that undersized height gets locked in forever
// on pages that embed multiple widgets (see idance.se clipping bug). Only
// scroll-into-view side effects are single-iframe-only, and those live in the
// parent's IFRAME_NAVIGATED handler, not here.

const PUBLIC_ACTIVITY_PAGE_SELECTOR = '[data-id="public-activity-page"]'

export type IframeHeightSyncConfig = {
  iframeId: string | null
  isMultipleIframes?: boolean
  forceNewTab?: boolean
  debug?: boolean
}

/** True when embed diagnostics should be logged (localhost, parent data-debug, or explicit flag). */
export const isIframeEmbedDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') return false
  if ((window as unknown as Record<string, unknown>).__IFRAME_EMBED_DEBUG) return true
  const config = (window as unknown as Record<string, unknown>).__IFRAME_CONFIG as IframeHeightSyncConfig | undefined
  if (config?.debug) return true
  const { hostname, search } = window.location
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true
  return new URLSearchParams(search).get('embedDebug') === 'true'
}

const logIframeEmbedDebug = (...args: unknown[]) => {
  if (isIframeEmbedDebugEnabled()) console.log(...args)
}

export const setupIframeHeightSync = (getIframeConfig: () => IframeHeightSyncConfig): (() => void) => {
  let lastSentHeight = 0

  const sendHeightToParent = () => {
    // Measure the actual content element instead of relying on scrollHeight
    // which can be clamped to the iframe viewport when using min-height: 100vh
    const contentEl = document.querySelector(PUBLIC_ACTIVITY_PAGE_SELECTOR)
    const height = contentEl
      ? contentEl.scrollHeight
      : Math.max(document.documentElement.scrollHeight, document.body.scrollHeight)

    // Only send if height actually changed
    if (height === lastSentHeight) return
    lastSentHeight = height

    // Prefer the ref value; fall back to the window-persisted config so that
    // pages remounting after SPA back-navigation still send the correct iframeId
    // even if their ref hasn't been restored yet.
    const windowConfig = (window as unknown as Record<string, unknown>).__IFRAME_CONFIG as
      | IframeHeightSyncConfig
      | undefined
    const iframeId = getIframeConfig().iframeId ?? windowConfig?.iframeId ?? 'unknown'

    const message = { type: 'adjustHeight', height, iframeId }
    window.parent.postMessage(message, '*')
    logIframeEmbedDebug(`📤 Sending height: ${height}px to parent for iframe: ${iframeId}`)
  }

  const initialTimeout = setTimeout(sendHeightToParent, 300)
  window.addEventListener('resize', sendHeightToParent)

  // Use ResizeObserver to detect actual size changes (more reliable than MutationObserver)
  const contentEl = document.querySelector(PUBLIC_ACTIVITY_PAGE_SELECTOR)
  const resizeObserver = new ResizeObserver(sendHeightToParent)
  if (contentEl) resizeObserver.observe(contentEl)
  resizeObserver.observe(document.body)

  // Keep MutationObserver as fallback for DOM changes that don't trigger resize
  const mutationObserver = new MutationObserver(() => {
    // Defer to next frame so layout has settled
    requestAnimationFrame(sendHeightToParent)
  })
  mutationObserver.observe(document.body, { childList: true, subtree: true })

  const messageHandler = (event: MessageEvent) => {
    if (!event.data) return
    const { type } = event.data
    if (type === 'requestHeightUpdate') {
      lastSentHeight = 0 // Force re-send
      sendHeightToParent()
    }
  }
  window.addEventListener('message', messageHandler)

  return () => {
    clearTimeout(initialTimeout)
    window.removeEventListener('resize', sendHeightToParent)
    window.removeEventListener('message', messageHandler)
    resizeObserver.disconnect()
    mutationObserver.disconnect()
  }
}
